import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { auth } from '../../../auth/appAuth';
import { changePassword } from '../../../api/localAuth';
import { getApiToken } from '../../../api/authToken';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { usePermissions } from '../../../company-management/hooks/usePermissions';
import { PERMISSIONS } from '../../../company-management/constants/permissions';
import { useTheme } from '@mui/material/styles';
import AvatarUpload from '../AvatarUpload';
import PrimaryButton from '../../../website/components/account-setup/shared/PrimaryButton';
import { sendCustomVerificationEmail } from '../../../auth/api/emailVerificationApi';
import AccountSettingsSecurity from './AccountSettings/AccountSettingsSecurity';
import { formatPhoneNumberInput } from '../../../utils/phoneFormat';
import { resolveTwoFactorPhone, maskTwoFactorPhone } from '../../../utils/twoFactorPhone';
import { upsertEmployee } from '../../../api/companyRecords';
import { patchCurrentUser } from '../../../api/me';
import { getEmailChangeCooldown, requestEmailChange } from '../../../auth/api/emailChangeApi';
import { updateEmailWithVerification } from '../../../auth/email-actions/emailUtils';
import './AccountSettings/AccountSettings.css';

const formatEmailChangeCooldown = (ms) => {
  const totalSec = Math.ceil(ms / 1000);
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  return d > 0 ? `${d}d ${h}h ${m}m` : `${h}h ${m}m`;
};

const AccountSettings = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [showPassword, setShowPassword] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const { setNotification } = useNotification();
  const { user, updateUser, companyData } = useAuth();
  const { hasPermission } = usePermissions();
  const canViewPrivacySettings = hasPermission(PERMISSIONS.SETTINGS.VIEW_PRIVACY);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [emailChangeCooldownMs, setEmailChangeCooldownMs] = useState(0);

  const cssVars = useMemo(() => {
    // Keep the UI consistent with the app theme without relying on MUI components.
    const bg = isDark ? '#0b1220' : '#f6f8fc';
    const surface = isDark ? '#0f172a' : '#ffffff';
    const surface2 = isDark ? 'rgba(15, 23, 42, 0.72)' : 'rgba(255, 255, 255, 0.7)';
    const text = isDark ? '#e5e7eb' : '#0f172a';
    const muted = isDark ? '#94a3b8' : '#64748b';
    const border = isDark ? 'rgba(148, 163, 184, 0.22)' : 'rgba(15, 23, 42, 0.12)';
    const shadow = isDark ? '0 10px 30px rgba(0,0,0,0.35)' : '0 10px 30px rgba(2, 6, 23, 0.08)';
    const inputBg = isDark ? 'rgba(2, 6, 23, 0.35)' : 'rgba(255, 255, 255, 0.9)';
    const inputBgFocus = isDark ? 'rgba(2, 6, 23, 0.55)' : '#ffffff';
    const shimmerA = isDark ? 'rgba(226, 232, 240, 0.08)' : 'rgba(15, 23, 42, 0.06)';
    const shimmerB = isDark ? 'rgba(226, 232, 240, 0.14)' : 'rgba(15, 23, 42, 0.12)';

    return {
      '--as-bg': bg,
      '--as-surface': surface,
      '--as-surface-2': surface2,
      '--as-text': text,
      '--as-muted': muted,
      '--as-border': border,
      '--as-shadow': shadow,
      '--as-focus': 'rgba(72, 138, 236, 0.45)',
      '--as-primary': '#488aec',
      '--as-input-bg': inputBg,
      '--as-input-bg-focus': inputBgFocus,
      '--as-shimmer-a': shimmerA,
      '--as-shimmer-b': shimmerB
    };
  }, [isDark]);

  useEffect(() => {
    if (user) {
      setEmail(user.email);
      setFirstName(user.firstName || '');
      setLastName(user.lastName || '');
      setPhone(
        formatPhoneNumberInput(
          resolveTwoFactorPhone(user)
        )
      );
      setIsInitialLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (resendCooldownSeconds <= 0) return;
    const t = setInterval(() => setResendCooldownSeconds(s => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldownSeconds]);

  useEffect(() => {
    let cancelled = false;
    getEmailChangeCooldown()
      .then((res) => {
        if (!cancelled) setEmailChangeCooldownMs(res?.remainingMs || 0);
      })
      .catch(() => {});
    const id = setInterval(() => {
      setEmailChangeCooldownMs((ms) => (ms > 1000 ? ms - 1000 : 0));
    }, 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const handleResendVerification = useCallback(async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setNotification({ open: true, message: 'Please sign in to resend the verification email.', severity: 'error' });
      return;
    }
    setResendLoading(true);
    try {
      const idToken = await currentUser.getIdToken();
      await sendCustomVerificationEmail(idToken);
      setResendCooldownSeconds(120);
      setNotification({ open: true, message: 'Verification email sent. Check your inbox (and spam folder).', severity: 'success' });
    } catch (err) {
      const sec = err?.retryAfterSeconds;
      if (typeof sec === 'number' && sec > 0) {
        setResendCooldownSeconds(sec);
        setNotification({ open: true, message: `Please wait ${sec} seconds before requesting another email.`, severity: 'warning' });
      } else {
        setNotification({ open: true, message: err?.message || 'Failed to send. Try again later.', severity: 'error' });
      }
    } finally {
      setResendLoading(false);
    }
  }, []);

  const handleSave = async () => {
    if (!user) {
      setNotification({ open: true, message: 'No authenticated user', severity: 'error' });
      return;
    }

    const normalizedEmail = (email || '').trim();
    const normalizedFirst = (firstName || '').trim();
    const normalizedLast = (lastName || '').trim();
    const normalizedPhone = (phone || '').trim();

    const isEmailChanging = (user.email || '').trim() !== normalizedEmail;
    const isPasswordChanging = Boolean(newPassword);

    if (!normalizedEmail) {
      setNotification({ open: true, message: 'Email is required', severity: 'error' });
      return;
    }

    if (isPasswordChanging && newPassword !== confirmNewPassword) {
      setNotification({ open: true, message: "Passwords don't match", severity: 'error' });
      return;
    }

    if ((isEmailChanging || isPasswordChanging) && !currentPassword) {
      setNotification({
        open: true,
        message: 'Current password is required to change email or password',
        severity: 'error'
      });
      return;
    }

    if (isEmailChanging && emailChangeCooldownMs > 0) {
      setNotification({
        open: true,
        message: `You can change your email again in ${formatEmailChangeCooldown(emailChangeCooldownMs)}.`,
        severity: 'error'
      });
      return;
    }

    setIsSaving(true);
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('No authenticated user');

      if (isEmailChanging) {
        const data = await requestEmailChange();
        if (!data?.allowed) {
          const remaining = data?.remainingMs || 0;
          setEmailChangeCooldownMs(remaining);
          throw new Error(`You can change your email again in ${formatEmailChangeCooldown(remaining)}.`);
        }
        await updateEmailWithVerification(currentUser, normalizedEmail);
        setEmailChangeCooldownMs(30 * 24 * 60 * 60 * 1000);
      }

      if (isPasswordChanging) {
        await changePassword({
          currentPassword,
          password: newPassword,
          token: await getApiToken(),
        });
      }

      await patchCurrentUser({
        firstName: normalizedFirst,
        lastName: normalizedLast,
        phone: normalizedPhone,
      });
      
      // Also update the employee record in Postgres
      if (user.companyId && user.uid) {
        await upsertEmployee({
          companyId: user.companyId,
          id: user.uid,
          employee: {
            firstName: normalizedFirst,
            lastName: normalizedLast,
            phone: normalizedPhone,
            updatedAt: new Date().toISOString(),
          },
        });
      }
      
      // Update local user state
      updateUser({
        ...user,
        email: isEmailChanging ? user.email : normalizedEmail,
        firstName: normalizedFirst,
        lastName: normalizedLast,
        phone: normalizedPhone
      });
      if (isEmailChanging) {
        setEmail(user.email || '');
      }

      // Clear sensitive fields after successful save
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
      setNotification({
        open: true,
        message: isEmailChanging
          ? 'We sent a verification link to your new email. Please verify to complete the change.'
          : 'Account settings updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating settings:', error);
      setNotification({ open: true, message: `Error updating settings: ${error.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const passwordsMismatch = newPassword !== confirmNewPassword && confirmNewPassword !== '';

  const EyeIcon = ({ off }) => (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {off ? (
        <>
          <path
            d="M4 4l16 16"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M10.6 10.6a3 3 0 004.24 4.24"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <path
            d="M7.1 7.1C4.9 8.7 3.3 10.8 2.5 12c1.6 2.4 5.2 7 9.5 7 1.2 0 2.3-.3 3.3-.7"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9.9 5.2c.7-.1 1.4-.2 2.1-.2 4.3 0 7.9 4.6 9.5 7-.6 1-1.7 2.6-3.2 4"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </>
      ) : (
        <>
          <path
            d="M2.5 12c1.6-2.4 5.2-7 9.5-7s7.9 4.6 9.5 7c-1.6 2.4-5.2 7-9.5 7s-7.9-4.6-9.5-7z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
          <path
            d="M12 15a3 3 0 100-6 3 3 0 000 6z"
            stroke="currentColor"
            strokeWidth="2"
          />
        </>
      )}
    </svg>
  );

  if (isInitialLoading) {
    return (
      <div className="accountSettingsPage" style={cssVars}>
        <div className="accountSettingsInner">
          <div className="asLoading" aria-busy="true" aria-live="polite">
            <div className="asLoadingCard">
              <div className="asShimmerLine short" />
              <div className="asShimmerLine medium" />
              <div className="asShimmerLine" />
              <div className="asShimmerLine" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="accountSettingsPage" style={cssVars}>
      <div className="accountSettingsInner">
        <div className="accountSettingsHeader">
          <div>
            <h1>Account Settings</h1>
            <p>Update your profile details and sign-in information.</p>
          </div>
        </div>

        <div className="accountSettingsGrid">
          <section className="asCard accountSettingsCard" aria-label="Profile and security">
            <div className="asCardBody">
              <div className="asSection" aria-label="Profile">
                <h2 className="asSectionTitle">Profile</h2>
                <p className="asSectionSub">Your photo and basic information.</p>

                <AvatarUpload user={user} setNotification={setNotification} updateUser={updateUser} ownerId={companyData?.ownerId} />

                <div className="asFormGrid">
                  <div className="asField">
                    <div className="asFieldLabelRow">
                      <label className="asLabel" htmlFor="as-firstName">
                        First name
                      </label>
                    </div>
                    <input
                      id="as-firstName"
                      className="asInput"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      autoComplete="given-name"
                      disabled={isSaving}
                    />
                  </div>

                  <div className="asField">
                    <div className="asFieldLabelRow">
                      <label className="asLabel" htmlFor="as-lastName">
                        Last name
                      </label>
                    </div>
                    <input
                      id="as-lastName"
                      className="asInput"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      autoComplete="family-name"
                      disabled={isSaving}
                    />
                  </div>

                  <div className="asField" style={{ gridColumn: '1 / -1' }}>
                    <div className="asFieldLabelRow">
                      <label className="asLabel" htmlFor="as-phone">
                        Phone number
                      </label>
                      <span className="asHint">Your mobile number for SMS login codes (not your business line)</span>
                    </div>
                    <input
                      id="as-phone"
                      className="asInput"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(formatPhoneNumberInput(e.target.value))}
                      autoComplete="tel"
                      placeholder="(555) 555-5555"
                      disabled={isSaving}
                    />
                  </div>
                </div>

                <p className="asHelperText" style={{ marginTop: 12 }}>
                  Tip: Use your personal mobile number here. Business phone numbers are not used for SMS two-factor authentication.
                </p>
              </div>

              <div className="asSection" aria-label="Security and privacy">
                <h2 className="asSectionTitle">Security & privacy</h2>
                <p className="asSectionSub">Manage verification, two-factor authentication, and support access.</p>

                <AccountSettingsSecurity
                  user={user}
                  canViewPrivacySettings={canViewPrivacySettings}
                  resendCooldownSeconds={resendCooldownSeconds}
                  resendLoading={resendLoading}
                  onResendVerification={handleResendVerification}
                  onUserUpdate={(patch) => updateUser((prev) => ({ ...(prev || {}), ...patch }))}
                />
              </div>
            </div>
          </section>

          <div className="accountSettingsCol2">
            <section className="asCard" aria-label="Sign in">
              <div className="asCardHeader">
                <h2>Sign-in</h2>
                <p className="sub">Change your email and password. Current password required for secure changes.</p>
              </div>
              <div className="asCardBody">
                <div className="asField">
                  <div className="asFieldLabelRow">
                    <label className="asLabel" htmlFor="as-email">
                      Email
                    </label>
                    <span className="asHint">Used to sign in</span>
                  </div>
                    <input
                    id="as-email"
                    className="asInput"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    disabled={isSaving || emailChangeCooldownMs > 0}
                  />
                  {emailChangeCooldownMs > 0 ? (
                    <p className="asHelperText">
                      You can change your email again in {formatEmailChangeCooldown(emailChangeCooldownMs)}.
                    </p>
                  ) : (
                    <p className="asHelperText">Changing email sends a verification link to the new address.</p>
                  )}
                </div>

                <div className="asDivider" />

                <div className="asFormGrid">
                  <div className="asField" style={{ gridColumn: '1 / -1' }}>
                    <div className="asFieldLabelRow">
                      <label className="asLabel" htmlFor="as-newPassword">
                        New password
                      </label>
                      <span className="asHint">Optional</span>
                    </div>
                    <div className="asInputWrap">
                      <input
                        id="as-newPassword"
                        className="asInput"
                        type={showPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        autoComplete="new-password"
                        disabled={isSaving}
                        style={{ paddingRight: 52 }}
                      />
                      <button
                        type="button"
                        className="asTrailingBtn"
                        onClick={() => setShowPassword(v => !v)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        disabled={isSaving}
                        title={showPassword ? 'Hide' : 'Show'}
                      >
                        <EyeIcon off={showPassword} />
                      </button>
                    </div>
                    <p className="asHelperText">Use 8+ characters with a mix of letters and numbers.</p>
                  </div>

                  <div className="asField" style={{ gridColumn: '1 / -1' }}>
                    <div className="asFieldLabelRow">
                      <label className="asLabel" htmlFor="as-confirmNewPassword">
                        Confirm new password
                      </label>
                    </div>
                    <input
                      id="as-confirmNewPassword"
                      className="asInput"
                      type={showPassword ? 'text' : 'password'}
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      autoComplete="new-password"
                      disabled={isSaving}
                      aria-invalid={passwordsMismatch ? 'true' : 'false'}
                    />
                    {passwordsMismatch ? (
                      <p className="asErrorText">Passwords don’t match.</p>
                    ) : (
                      <p className="asHelperText">Re-type to confirm.</p>
                    )}
                  </div>

                  <div className="asField" style={{ gridColumn: '1 / -1' }}>
                    <div className="asFieldLabelRow">
                      <label className="asLabel" htmlFor="as-currentPassword">
                        Current password
                      </label>
                      <span className="asHint">Required for email/password changes</span>
                    </div>
                    <input
                      id="as-currentPassword"
                      className="asInput"
                      type="password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      autoComplete="current-password"
                      disabled={isSaving}
                    />
                  </div>
                </div>
              </div>
            </section>

            <div className="asActions">
              <span className="asSavingNote">{isSaving ? 'Saving changes…' : ''}</span>
              <PrimaryButton
                onClick={handleSave}
                disabled={isSaving}
                style={{
                  width: 220,
                  pointerEvents: isSaving ? 'none' : 'auto'
                }}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;